import { useState } from 'react';
import { PlusIcon, PencilIcon, KeyIcon } from '@heroicons/react/24/outline';
import Card, { CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Avatar from '@/components/ui/Avatar';
import Modal, { ModalBody, ModalFooter } from '@/components/ui/Modal';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import {
  TableContainer, Table, TableHead, TableBody, TableRow, TableHeaderCell, TableCell,
} from '@/components/ui/Table';

const mockUsers = [
  { id: '1', username: 'admin', email: 'admin@kampus.com', role: 'admin', org: 'Éditions Kampus', status: 'active' },
  { id: '2', username: 'publisher1', email: 'pub1@kampus.com', role: 'client', org: 'Éditions Kampus', status: 'active' },
  { id: '3', username: 'service_bot', email: 'bot@kampus.com', role: 'service', org: null, status: 'active' },
  { id: '4', username: 'editor', email: 'editor@techmedia.com', role: 'client', org: 'TechMedia Group', status: 'inactive' },
];

const roleColors: Record<string, 'primary' | 'secondary' | 'slate'> = {
  admin: 'primary',
  client: 'secondary',
  service: 'slate',
};

export default function AdminUsersPage() {
  const [showModal, setShowModal] = useState(false);
  const [roleFilter, setRoleFilter] = useState('');

  const filteredUsers = roleFilter ? mockUsers.filter(u => u.role === roleFilter) : mockUsers;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">User Management</h1>
          <p className="text-slate-500 mt-1">Manage users and their permissions</p>
        </div>
        <Button leftIcon={<PlusIcon className="w-4 h-4" />} onClick={() => setShowModal(true)}>
          Add User
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardBody className="text-center">
          <p className="text-3xl font-bold text-slate-800">{mockUsers.length}</p>
          <p className="text-sm text-slate-500">Total Users</p>
        </CardBody></Card>
        <Card><CardBody className="text-center">
          <p className="text-3xl font-bold text-primary-500">{mockUsers.filter(u => u.role === 'admin').length}</p>
          <p className="text-sm text-slate-500">Admins</p>
        </CardBody></Card>
        <Card><CardBody className="text-center">
          <p className="text-3xl font-bold text-secondary-500">{mockUsers.filter(u => u.role === 'client').length}</p>
          <p className="text-sm text-slate-500">Clients</p>
        </CardBody></Card>
        <Card><CardBody className="text-center">
          <p className="text-3xl font-bold text-success-500">{mockUsers.filter(u => u.status === 'active').length}</p>
          <p className="text-sm text-slate-500">Active</p>
        </CardBody></Card>
      </div>

      <Card padding="md">
        <Select
          options={[
            { value: '', label: 'All Roles' },
            { value: 'admin', label: 'Admin' },
            { value: 'client', label: 'Client' },
            { value: 'service', label: 'Service' },
          ]}
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="w-40"
        />
      </Card>

      <Card padding="none">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow isHoverable={false}>
                <TableHeaderCell>User</TableHeaderCell>
                <TableHeaderCell>Email</TableHeaderCell>
                <TableHeaderCell>Role</TableHeaderCell>
                <TableHeaderCell>Organization</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Actions</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar name={user.username} size="sm" />
                      <span className="font-medium">{user.username}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-600">{user.email}</TableCell>
                  <TableCell>
                    <Badge variant={roleColors[user.role]}>{user.role}</Badge>
                  </TableCell>
                  <TableCell className="text-slate-600">{user.org || '--'}</TableCell>
                  <TableCell>
                    <Badge variant={user.status === 'active' ? 'success' : 'slate'}>{user.status}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" leftIcon={<PencilIcon className="w-3 h-3" />}>Edit</Button>
                      <Button variant="ghost" size="sm" leftIcon={<KeyIcon className="w-3 h-3" />}>API Key</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Add User">
        <ModalBody className="space-y-4">
          <Input label="Username" placeholder="Enter username" />
          <Input label="Email" type="email" placeholder="Enter email" />
          <Input label="Password" type="password" placeholder="Enter password" />
          <Select
            label="Role"
            options={[
              { value: 'client', label: 'Client' },
              { value: 'admin', label: 'Admin' },
              { value: 'service', label: 'Service' },
            ]}
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button onClick={() => setShowModal(false)}>Create User</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

